import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Home, Building2, Sparkles, Phone, ArrowRight, ChevronDown, UtensilsCrossed, Shirt, SunDim, Sparkles as SparklesIcon, DoorOpen, Warehouse, Wind, PanelTop, Blinds, Trash2, Fence, BedDouble } from 'lucide-react';
import { Navbar } from '../components/Navbar';
import { Footer } from '../components/Footer';
import { SEOHead } from '../components/SEOHead';
import { BreadcrumbSchema } from '../components/StructuredData';
import { ServiceNav } from '../components/ServiceNav';
const cardClass = 'bg-white rounded-2xl border border-gray-200 p-8 md:p-10 shadow-sm';
const stepHeadingClass = 'font-bebas text-2xl md:text-3xl text-[#1a2744] pb-3 border-b border-gray-200';
const labelClass = 'text-[#1a2744] font-medium text-sm';
const inputClass = 'w-full border border-gray-200 rounded-lg px-4 py-3 bg-white text-[#1a2744] focus:outline-none focus:border-[#e91e8c] focus:ring-1 focus:ring-[#e91e8c] transition-all placeholder-gray-400';
const selectClass = 'w-full border border-gray-200 rounded-lg px-4 py-3 bg-white text-[#1a2744] focus:outline-none focus:border-[#e91e8c] focus:ring-1 focus:ring-[#e91e8c] transition-all appearance-none cursor-pointer';
type PillGroupProps = {
  options: string[];
  selected: string | string[];
  onSelect: (val: string) => void;
  multi?: boolean;
};
function PillGroup({
  options,
  selected,
  onSelect,
  multi = false
}: PillGroupProps) {
  const isSelected = (opt: string) => multi ? (selected as string[]).includes(opt) : selected === opt;
  return <div className="flex flex-wrap gap-3" data-id="element-363">
      {options.map(opt => <button key={opt} type="button" onClick={() => onSelect(opt)} className={`px-5 py-2.5 rounded-full text-sm font-medium transition-all border ${isSelected(opt) ? 'bg-[#e91e8c] border-[#e91e8c] text-white shadow-sm' : 'bg-white border-gray-200 text-gray-600 hover:border-[#e91e8c] hover:text-[#e91e8c]'}`} data-id="element-364">
          {opt}
        </button>)}
    </div>;
}
const extras = [{
  name: 'Washing dishes',
  price: 7,
  icon: UtensilsCrossed
}, {
  name: 'Change Sheets',
  price: 7,
  icon: BedDouble
}, {
  name: 'Laundry (wash & fold)',
  price: 7,
  icon: Shirt
}, {
  name: 'Inside facade of windows',
  price: 7,
  icon: SunDim
}, {
  name: 'First or Deep Cleaning',
  price: 7,
  icon: SparklesIcon
}, {
  name: 'Interior & Exterior of Kitchen Cabinets',
  price: 7,
  icon: DoorOpen
}, {
  name: 'Finished Basement',
  price: 7,
  icon: Warehouse
}, {
  name: 'Flu Buster - Eucalyptus',
  price: 7,
  icon: Wind
}, {
  name: 'Hand Wipe Baseboards',
  price: 7,
  icon: PanelTop
}, {
  name: 'Interior Window Glass',
  price: 7,
  icon: SunDim
}, {
  name: 'Wet Wipe Blinds',
  price: 7,
  icon: Blinds
}, {
  name: 'Property Cleanup',
  price: 7,
  icon: Trash2
}, {
  name: 'Patio or Balcony',
  price: 7,
  icon: Fence
}, {
  name: 'Carpet Steam Clean - Entire Home',
  price: 7,
  icon: SparklesIcon
}, {
  name: 'Area Rugs Steam Cleaning',
  price: 7,
  icon: SparklesIcon
}];
const timeSlots = ['14:00', '15:00'];
export function AirbnbQuote() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);
  // Step 1 - Contact
  const [contact, setContact] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: ''
  });
  const handleContactChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setContact({
      ...contact,
      [e.target.name]: e.target.value
    });
  };
  // Step 2 - Location
  const [location, setLocation] = useState({
    address: '',
    unit: '',
    city: '',
    postalCode: ''
  });
  const handleLocationChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setLocation({
      ...location,
      [e.target.name]: e.target.value
    });
  };
  // Step 3 - Cleaning details
  const [hours, setHours] = useState('2 Hours');
  const [cleaners, setCleaners] = useState('2 Cleaners');
  const [bedrooms, setBedrooms] = useState('Studio / 1 Bedroom');
  const [bathrooms, setBathrooms] = useState('1 Bathroom');
  const [parking, setParking] = useState('Free on street');
  const [lastCleaning, setLastCleaning] = useState('< 1 month');
  const [pets, setPets] = useState<string[]>(['None']);
  const [supplies, setSupplies] = useState('Yes');
  const handlePetToggle = (pet: string) => {
    if (pet === 'None') {
      setPets(['None']);
    } else {
      setPets(prev => {
        const newPets = prev.filter(p => p !== 'None');
        if (newPets.includes(pet)) {
          return newPets.filter(p => p !== pet).length === 0 ? ['None'] : newPets.filter(p => p !== pet);
        }
        return [...newPets, pet];
      });
    }
  };
  // Step 4 - Extras
  const [selectedExtras, setSelectedExtras] = useState<string[]>([]);
  const toggleExtra = (name: string) => {
    setSelectedExtras(prev => prev.includes(name) ? prev.filter(e => e !== name) : [...prev, name]);
  };
  // Step 5 - Booking Bundle
  const [bundle, setBundle] = useState('1 Service');
  // Step 6 - Date & Time
  const [date, setDate] = useState('');
  const [timeSlot, setTimeSlot] = useState('');
  const [additionalDetails, setAdditionalDetails] = useState('');
  return <div className="min-h-screen w-full font-sans bg-white flex flex-col" data-id="element-365">
      <SEOHead title="Book Airbnb / Short Term Rental Cleaning | Smith & Jengs Cleaning Services" description="Book your Airbnb or Short Term Rental cleaning in Bicester and Oxfordshire. Fast, reliable turnover cleaning in 60 seconds." canonical="https://smithandjengs.com/airbnb-quote" data-id="element-366" />
      <BreadcrumbSchema items={[{
      name: 'Home',
      url: 'https://smithandjengs.com/'
    }, {
      name: 'Airbnb / Short Term Rental',
      url: 'https://smithandjengs.com/airbnb-quote'
    }, {
      name: 'Book Online',
      url: 'https://smithandjengs.com/airbnb-quote'
    }]} data-id="element-367" />
      <Navbar data-id="element-368" />

      <main className="flex-1 pt-24" data-id="element-369">
        <ServiceNav data-id="element-370" />

        {/* Header */}
        <section className="w-full bg-white pt-12 md:pt-16 pb-12 px-4 md:px-12" data-id="element-371">
          <motion.div initial={{
          opacity: 0,
          y: 30
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.8
        }} className="max-w-5xl w-full mx-auto text-center" data-id="element-372">
            <h1 className="font-bebas text-[#1a2744] leading-[0.85] tracking-wide mb-6" data-id="element-373">
              <span className="text-5xl md:text-6xl lg:text-7xl block" data-id="element-374">
                BOOK ONLINE
              </span>
              <span className="text-3xl md:text-4xl lg:text-5xl block text-[#e91e8c]" data-id="element-375">
                AIRBNB / SHORT TERM RENTAL
              </span>
            </h1>
            <p className="text-gray-600 font-medium max-w-2xl mx-auto text-lg" data-id="element-376">
              Book your Airbnb or Short Term Rental cleaning in 60 seconds.
            </p>
          </motion.div>
        </section>

        {/* Form Section */}
        <section className="w-full bg-[#f8f9fa] py-12 md:py-16 px-4 md:px-8" id="booking-form" data-id="element-377">
          <motion.div initial={{
          opacity: 0,
          y: 30
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.8,
          delay: 0.2
        }} className="max-w-4xl w-full mx-auto" data-id="element-378">
            <form className="space-y-8" onSubmit={e => e.preventDefault()} data-id="element-379">
              {/* STEP 1: Contact */}
              <div className={cardClass} data-id="element-380">
                <h3 className={stepHeadingClass} data-id="element-381">
                  <span className="text-[#e91e8c] mr-2" data-id="element-382">STEP 1:</span> Contact
                  Information
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-8" data-id="element-383">
                  <div className="space-y-2" data-id="element-384">
                    <label className={labelClass} data-id="element-385">First Name</label>
                    <input type="text" name="firstName" value={contact.firstName} onChange={handleContactChange} className={inputClass} placeholder="John" data-id="element-386" />
                  </div>
                  <div className="space-y-2" data-id="element-387">
                    <label className={labelClass} data-id="element-388">Last Name</label>
                    <input type="text" name="lastName" value={contact.lastName} onChange={handleContactChange} className={inputClass} placeholder="Doe" data-id="element-389" />
                  </div>
                  <div className="space-y-2" data-id="element-390">
                    <label className={labelClass} data-id="element-391">Email</label>
                    <input type="email" name="email" value={contact.email} onChange={handleContactChange} className={inputClass} placeholder="john@example.com" data-id="element-392" />
                  </div>
                  <div className="space-y-2" data-id="element-393">
                    <label className={labelClass} data-id="element-394">Phone Number</label>
                    <div className="flex" data-id="element-395">
                      <div className="flex items-center justify-center px-4 border border-r-0 border-gray-200 rounded-l-lg bg-gray-50 text-gray-500" data-id="element-396">
                        🇬🇧
                      </div>
                      <input type="tel" name="phone" value={contact.phone} onChange={handleContactChange} className={`${inputClass} rounded-l-none`} placeholder="07000 000000" data-id="element-397" />
                    </div>
                  </div>
                </div>
              </div>

              {/* STEP 2: Location */}
              <div className={cardClass} data-id="element-398">
                <h3 className={stepHeadingClass} data-id="element-399">
                  <span className="text-[#e91e8c] mr-2" data-id="element-400">STEP 2:</span> Cleaning
                  Location
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-8" data-id="element-401">
                  <div className="space-y-2" data-id="element-402">
                    <label className={labelClass} data-id="element-403">Address</label>
                    <input type="text" name="address" value={location.address} onChange={handleLocationChange} className={inputClass} placeholder="Enter a location" data-id="element-404" />
                  </div>
                  <div className="space-y-2" data-id="element-405">
                    <label className={labelClass} data-id="element-406">Unit/Apt/Suite #</label>
                    <input type="text" name="unit" value={location.unit} onChange={handleLocationChange} className={inputClass} placeholder="Optional" data-id="element-407" />
                  </div>
                  <div className="space-y-2" data-id="element-408">
                    <label className={labelClass} data-id="element-409">City</label>
                    <input type="text" name="city" value={location.city} onChange={handleLocationChange} className={inputClass} placeholder="Bicester" data-id="element-410" />
                  </div>
                  <div className="space-y-2" data-id="element-411">
                    <label className={labelClass} data-id="element-412">Postal Code</label>
                    <input type="text" name="postalCode" value={location.postalCode} onChange={handleLocationChange} className={inputClass} placeholder="OX26" data-id="element-413" />
                  </div>
                </div>
              </div>

              {/* STEP 3: Details */}
              <div className={cardClass} data-id="element-414">
                <h3 className={stepHeadingClass} data-id="element-415">
                  <span className="text-[#e91e8c] mr-2" data-id="element-416">STEP 3:</span> Tell us
                  about the cleaning
                </h3>
                <div className="space-y-8 mt-8" data-id="element-417">
                  <div className="space-y-4" data-id="element-418">
                    <label className={labelClass} data-id="element-419">
                      How many hours and cleaners would you like to book?
                    </label>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-id="element-420">
                      <div className="relative" data-id="element-421">
                        <select value={hours} onChange={e => setHours(e.target.value)} className={selectClass} data-id="element-422">
                          <option data-id="element-423">2 Hours</option>
                          <option data-id="element-424">3 Hours</option>
                          <option data-id="element-425">4 Hours</option>
                          <option data-id="element-426">5 Hours</option>
                          <option data-id="element-427">6 Hours</option>
                          <option data-id="element-428">7 Hours</option>
                          <option data-id="element-429">8 Hours</option>
                        </select>
                        <ChevronDown className="absolute right-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400 pointer-events-none" data-id="element-430" />
                      </div>
                      <div className="relative" data-id="element-431">
                        <select value={cleaners} onChange={e => setCleaners(e.target.value)} className={selectClass} data-id="element-432">
                          <option data-id="element-433">1 Cleaner</option>
                          <option data-id="element-434">2 Cleaners</option>
                          <option data-id="element-435">3 Cleaners</option>
                          <option data-id="element-436">4 Cleaners</option>
                        </select>
                        <ChevronDown className="absolute right-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400 pointer-events-none" data-id="element-437" />
                      </div>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-6" data-id="element-438">
                    <div className="space-y-2" data-id="element-439">
                      <label className={labelClass} data-id="element-440">Bedrooms</label>
                      <div className="relative" data-id="element-441">
                        <select value={bedrooms} onChange={e => setBedrooms(e.target.value)} className={selectClass} data-id="element-442">
                          <option data-id="element-443">Studio / 1 Bedroom</option>
                          <option data-id="element-444">2 Bedrooms</option>
                          <option data-id="element-445">3 Bedrooms</option>
                          <option data-id="element-446">4 Bedrooms</option>
                          <option data-id="element-447">5+ Bedrooms</option>
                        </select>
                        <ChevronDown className="absolute right-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400 pointer-events-none" data-id="element-448" />
                      </div>
                    </div>
                    <div className="space-y-2" data-id="element-449">
                      <label className={labelClass} data-id="element-450">Bathrooms</label>
                      <div className="relative" data-id="element-451">
                        <select value={bathrooms} onChange={e => setBathrooms(e.target.value)} className={selectClass} data-id="element-452">
                          <option data-id="element-453">1 Bathroom</option>
                          <option data-id="element-454">2 Bathrooms</option>
                          <option data-id="element-455">3 Bathrooms</option>
                          <option data-id="element-456">4+ Bathrooms</option>
                        </select>
                        <ChevronDown className="absolute right-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400 pointer-events-none" data-id="element-457" />
                      </div>
                    </div>
                  </div>

                  <div className="space-y-3" data-id="element-458">
                    <label className={labelClass} data-id="element-459">Parking</label>
                    <PillGroup options={['Free on street', 'Free home parking', 'Parking meters', 'Paid parking lot']} selected={parking} onSelect={setParking} data-id="element-460" />
                  </div>

                  <div className="space-y-3" data-id="element-461">
                    <label className={labelClass} data-id="element-462">
                      When was your last full cleaning?
                    </label>
                    <PillGroup options={['< 1 month', "I'm too embarrassed to say"]} selected={lastCleaning} onSelect={setLastCleaning} data-id="element-463" />
                  </div>

                  <div className="space-y-3" data-id="element-464">
                    <label className={labelClass} data-id="element-465">
                      Do you have any pets? (Required for safety & allergy
                      reasons)
                    </label>
                    <PillGroup options={['None', 'Dog', 'Cat']} selected={pets} onSelect={handlePetToggle} multi data-id="element-466" />
                  </div>

                  <div className="space-y-3" data-id="element-467">
                    <label className={labelClass} data-id="element-468">
                      All cleaning supplies are included at no extra charge.
                      Would you prefer we use yours?
                    </label>
                    <PillGroup options={['Yes', 'No']} selected={supplies} onSelect={setSupplies} data-id="element-469" />
                  </div>
                </div>
              </div>

              {/* STEP 4: Extras */}
              <div className={cardClass} data-id="element-470">
                <h3 className={stepHeadingClass} data-id="element-471">
                  <span className="text-[#e91e8c] mr-2" data-id="element-472">STEP 4:</span> Select
                  Extras
                </h3>
                <p className="text-gray-500 text-sm mt-2 mb-6" data-id="element-473">
                  Free up more of your precious time for what matters most!
                </p>
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4" data-id="element-474">
                  {extras.map(extra => {
                  const isSelected = selectedExtras.includes(extra.name);
                  return <button key={extra.name} type="button" onClick={() => toggleExtra(extra.name)} className={`flex flex-col items-center justify-center p-4 rounded-xl border-2 transition-all ${isSelected ? 'border-[#e91e8c] bg-[#e91e8c]/5' : 'border-gray-100 bg-white hover:border-[#e91e8c]/30 hover:bg-gray-50'}`} data-id="element-475">
                        <div className={`w-12 h-12 rounded-full flex items-center justify-center mb-3 transition-colors ${isSelected ? 'bg-[#e91e8c] text-white' : 'bg-[#1a2744] text-white'}`} data-id="element-476">
                          <extra.icon className="w-6 h-6" data-id="element-477" />
                        </div>
                        <span className="text-sm font-medium text-center text-[#1a2744] leading-tight" data-id="element-478">
                          {extra.name}
                        </span>
                      </button>;
                })}
                </div>
              </div>

              {/* STEP 5: Booking Bundle */}
              <div className={cardClass} data-id="element-479">
                <h3 className={stepHeadingClass} data-id="element-480">
                  <span className="text-[#e91e8c] mr-2" data-id="element-481">STEP 5:</span> Booking
                  Bundle
                </h3>
                <p className="text-gray-500 text-sm mt-2 mb-6" data-id="element-482">
                  How many services would you like to purchase at this time?
                </p>
                <PillGroup options={['1 Service', '5 Services -5%', '10 Services -15%']} selected={bundle} onSelect={setBundle} data-id="element-483" />
              </div>

              {/* STEP 6: Date & Time */}
              <div className={cardClass} data-id="element-484">
                <h3 className={stepHeadingClass} data-id="element-485">
                  <span className="text-[#e91e8c] mr-2" data-id="element-486">STEP 6:</span> Pick a
                  date for the cleaning.
                </h3>
                <p className="text-gray-500 text-sm mt-2 mb-6" data-id="element-487">
                  For recurring services, each one will commence on the same day
                  and time as selected for the first service.
                </p>
                <div className="space-y-6" data-id="element-488">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-6" data-id="element-489">
                    <div className="space-y-2" data-id="element-490">
                      <label className={labelClass} data-id="element-491">Date</label>
                      <input type="date" value={date} onChange={e => setDate(e.target.value)} className={inputClass} data-id="element-492" />
                    </div>
                    <div className="space-y-2" data-id="element-493">
                      <label className={labelClass} data-id="element-494">Time</label>
                      <div className="relative" data-id="element-495">
                        <select value={timeSlot} onChange={e => setTimeSlot(e.target.value)} className={selectClass} data-id="element-496">
                          <option value="" data-id="element-497">Select a time</option>
                          {timeSlots.map(slot => <option key={slot} value={slot} data-id="element-498">
                              {slot}
                            </option>)}
                        </select>
                        <ChevronDown className="absolute right-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400 pointer-events-none" data-id="element-499" />
                      </div>
                    </div>
                  </div>
                  <div className="space-y-2" data-id="element-500">
                    <label className={labelClass} data-id="element-501">
                      Are there any additional details we need to know?
                    </label>
                    <textarea value={additionalDetails} onChange={e => setAdditionalDetails(e.target.value)} className={`${inputClass} min-h-[120px] resize-y`} placeholder="Any special instructions..." data-id="element-502" />
                  </div>
                </div>
              </div>

              {/* STEP 7: Submit */}
              <div className="flex justify-center pt-4" data-id="element-503">
                <button type="submit" className="bg-[#e91e8c] hover:bg-[#d0187c] text-white px-12 py-4 rounded-full font-semibold tracking-wide transition-colors shadow-md flex items-center gap-3 text-lg" data-id="element-504">
                  Book Now <ArrowRight className="w-5 h-5" data-id="element-505" />
                </button>
              </div>
            </form>
          </motion.div>
        </section>

        {/* Explore Our Services */}
        <section className="w-full bg-white py-20 px-4 md:px-12 border-t border-gray-200" data-id="element-506">
          <div className="max-w-7xl mx-auto" data-id="element-507">
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.6
          }} className="text-center mb-16" data-id="element-508">
              <h2 className="font-bebas text-4xl md:text-5xl lg:text-6xl text-[#1a2744] tracking-wide" data-id="element-509">
                EXPLORE OUR <span className="text-[#e91e8c]" data-id="element-510">SERVICES</span>
              </h2>
              <p className="text-gray-600 mt-4 max-w-2xl mx-auto" data-id="element-511">
                Discover our full range of professional cleaning solutions
                tailored to your needs.
              </p>
            </motion.div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6" data-id="element-512">
              {[{
              title: 'Basic Home Cleaning',
              desc: 'Regular maintenance to keep your home spotless.',
              link: '/basic-home-cleaning',
              icon: Home
            }, {
              title: 'Spring / Deep Cleaning',
              desc: 'Thorough, top-to-bottom detail cleaning.',
              link: '/spring-deep-cleaning',
              icon: Sparkles
            }, {
              title: 'Commercial Cleaning',
              desc: 'Professional cleaning for your business.',
              link: '/commercial-cleaning',
              icon: Building2
            }, {
              title: 'Contact Us',
              desc: 'Get in touch for custom requests.',
              link: '/contact-us',
              icon: Phone
            }].map((service, idx) => <motion.div key={idx} initial={{
              opacity: 0,
              y: 20
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true
            }} transition={{
              duration: 0.5,
              delay: idx * 0.1
            }} data-id="element-513">
                  <Link to={service.link} className="group block h-full bg-[#f8f9fa] rounded-xl p-8 hover:shadow-md transition-all border border-gray-100 hover:border-[#5bbcd5]" data-id="element-514">
                    <div className="w-12 h-12 rounded-full bg-white flex items-center justify-center mb-6 group-hover:bg-[#5bbcd5]/10 transition-colors" data-id="element-515">
                      <service.icon className="w-6 h-6 text-[#1a2744] group-hover:text-[#5bbcd5] transition-colors" data-id="element-516" />
                    </div>
                    <h3 className="font-bebas text-2xl text-[#1a2744] mb-3 tracking-wide group-hover:text-[#e91e8c] transition-colors" data-id="element-517">
                      {service.title}
                    </h3>
                    <p className="text-gray-500 text-sm mb-6 line-clamp-2" data-id="element-518">
                      {service.desc}
                    </p>
                    <div className="flex items-center text-[#5bbcd5] font-medium text-sm mt-auto" data-id="element-519">
                      Learn more
                      <ArrowRight className="w-4 h-4 ml-2 transform group-hover:translate-x-1 transition-transform" data-id="element-520" />
                    </div>
                  </Link>
                </motion.div>)}
            </div>
          </div>
        </section>
      </main>

      <Footer data-id="element-521" />
    </div>;
}